import { AuthError } from '@supabase/supabase-js';
import { createLogger } from '../utils/logger';
import i18n from '../locales';
import { captureMessage } from './sentry';

/**
 * Global error handler for Supabase API calls
 *
 * Handles:
 * - User deleted from database (403 + profile not found) → Silent logout
 * - Network errors and outages → Info dialog
 * - Other errors → Pass through normally
 */

const logger = createLogger('ErrorHandler');

interface DialogInterface {
  alert: (title: string, message: string, variant?: 'success' | 'error' | 'warning' | 'info') => void;
}

interface AuthServiceInterface {
  signOut: () => Promise<void>;
}

class SupabaseErrorHandler {
  private static instance: SupabaseErrorHandler;
  private dialog: DialogInterface | null = null;
  private authService: AuthServiceInterface | null = null;
  private originalFetch: typeof fetch;

  private constructor() {
    // Store original fetch for restoration if needed
    this.originalFetch = global.fetch;
    logger.debug('ErrorHandler constructor called');
  }

  static getInstance(): SupabaseErrorHandler {
    if (!SupabaseErrorHandler.instance) {
      SupabaseErrorHandler.instance = new SupabaseErrorHandler();
    }
    return SupabaseErrorHandler.instance;
  }

  /**
   * Initialize the error handler with required dependencies
   * Must be called from App.tsx after DialogProvider is mounted
   */
  initialize(dialog: DialogInterface, authService: AuthServiceInterface): void {
    logger.debug('Initializing error handler with dialog and auth service');
    this.dialog = dialog;
    this.authService = authService;
    logger.debug('Error handler initialized successfully');
  }

  /**
   * Install the global fetch interceptor
   * Wraps all fetch calls to intercept Supabase errors
   */
  installInterceptor(): void {
    logger.debug('Installing global fetch interceptor');
    const originalFetch = this.originalFetch;
    const handler = this;

    // Override global fetch
    global.fetch = async function (...args: Parameters<typeof fetch>): Promise<Response> {
      const url = args[0]?.toString() || '';
      let responseReceived = false;

      try {
        const response = await originalFetch(...args);
        responseReceived = true; // Mark that HTTP response was received

        // Only intercept Supabase API calls
        const isSupabaseCall = url.includes('/rest/v1/') || url.includes('/auth/v1/');

        if (isSupabaseCall) {
          logger.debug(`Supabase call detected: ${url} | Status: ${response.status}`);

          if (!response.ok) {
            logger.debug(`Supabase error response: ${response.status} for ${url}`);
            await handler.handleSupabaseError(response.clone(), url);
          }
        }

        return response;
      } catch (error) {
        // Only treat as network error if we never received an HTTP response
        // This prevents misclassifying client-side processing errors (e.g., JSON parsing)
        // as network errors
        if (!responseReceived) {
          // Only show dialog for Supabase API failures — not for third-party services
          // (Sentry, analytics, etc.) that may be blocked by privacy browsers/ad blockers
          const isSupabaseCall =
            url.includes('/rest/v1/') ||
            url.includes('/auth/v1/') ||
            url.includes('/functions/v1/');
          if (isSupabaseCall) {
            logger.debug(`Network error for Supabase call ${url}:`, error);
            await handler.handleNetworkError(error);
          } else {
            logger.debug(`Network error for third-party call silently ignored: ${url}`);
          }
        } else {
          // Response was received but error occurred during processing
          // Let it bubble up naturally without showing misleading "connection error" dialog
          logger.debug(`Processing error after response received for ${url}:`, error);
        }
        throw error;
      }
    };

    logger.debug('Global fetch interceptor installed successfully');
  }

  /**
   * Handle Supabase API errors (non-2xx responses)
   */
  private async handleSupabaseError(response: Response, url: string): Promise<void> {
    const status = response.status;
    logger.debug(`Handling Supabase error - Status: ${status}, URL: ${url}`);

    // Handle 403 Forbidden - could be deleted user or permission issue
    if (status === 403) {
      logger.debug('Processing 403 Forbidden error');
      try {
        const errorData = await response.json();
        logger.debug('Error data:', errorData);

        // Check if this is a "user deleted" scenario
        // Supabase returns 403 when user auth is valid but profile doesn't exist
        if (this.isUserDeletedError(errorData, url)) {
          logger.debug('Detected deleted user scenario');
          this.handleDeletedUser(); // Non-blocking call
          return;
        }

        // Check if this is a session/token issue
        if (this.isSessionError(errorData)) {
          logger.debug('Detected session error');
          this.showNetworkDialog(
            i18n.t('common.errors.sessionExpired'),
            i18n.t('common.errors.sessionExpiredMessage')
          );
          this.handleDeletedUser(); // Logout on session expiry too (non-blocking)
          return;
        }

        // Other 403 errors - just show a generic message
        logger.debug('Generic 403 error - showing access denied dialog');
        this.showNetworkDialog(
          i18n.t('common.errors.accessDenied'),
          i18n.t('common.errors.accessDeniedMessage')
        );
      } catch (parseError) {
        // Failed to parse error response - treat as generic error
        logger.debug('Failed to parse Supabase error:', parseError);
      }
    }

    // Handle 500+ server errors (outages)
    if (status >= 500) {
      logger.debug(`Server error detected: ${status}`);
      captureMessage(`Server error: ${status}`, 'supabase_server_error');
      this.showNetworkDialog(
        i18n.t('common.errors.serviceUnavailable'),
        i18n.t('common.errors.serviceUnavailableMessage')
      );
    }

    // Handle 401 Unauthorized (expired token, etc.)
    if (status === 401) {
      logger.debug('401 Unauthorized - logging out user');
      this.showNetworkDialog(
        i18n.t('common.errors.authenticationRequired'),
        i18n.t('common.errors.authenticationRequiredMessage')
      );
      this.handleDeletedUser(); // Logout on auth failure (non-blocking)
    }
  }

  /**
   * Handle network errors (timeout, no connection, DNS failure)
   */
  private async handleNetworkError(error: unknown): Promise<void> {
    if (error instanceof TypeError) {
      // Network errors are usually TypeErrors in fetch
      // Common cases: no internet, DNS failure, timeout
      captureMessage('Network error detected', 'network_error');
      this.showNetworkDialog(
        i18n.t('common.errors.connectionError'),
        i18n.t('common.errors.checkConnection')
      );
    }
  }

  /**
   * Detect if error indicates user was deleted from database
   */
  private isUserDeletedError(errorData: any, url: string): boolean {
    logger.debug(`Checking if user deleted - URL: ${url}`);

    // Pattern 1: Auth endpoint returns 403 for valid JWT but deleted profile
    if (url.includes('/auth/v1/user')) {
      logger.debug('Pattern 1 matched: /auth/v1/user endpoint');
      return true;
    }

    // Pattern 2: Profile lookup returns no rows
    if (errorData?.message?.includes('profile') || errorData?.hint?.includes('no rows')) {
      logger.debug('Pattern 2 matched: profile-related error');
      return true;
    }

    // Pattern 3 REMOVED: Generic RLS violations (42501) are NOT always deleted users
    // They can be legitimate permission errors (e.g., buyer trying to update seller's offer)
    // Real deleted user scenarios should be caught by Patterns 1 & 2 above

    logger.debug('No deleted user pattern matched');
    return false;
  }

  /**
   * Detect if error is related to session/token issues
   */
  private isSessionError(errorData: any): boolean {
    const message = errorData?.message?.toLowerCase() || '';
    return (
      message.includes('jwt') ||
      message.includes('token') ||
      message.includes('expired') ||
      message.includes('invalid')
    );
  }

  /**
   * Handle deleted user - logout silently and let navigation handle redirect
   * IMPORTANT: Uses setTimeout to make logout non-blocking and ensure auth state propagates
   */
  private handleDeletedUser(): void {
    logger.debug('handleDeletedUser called - attempting to logout');

    if (!this.authService) {
      logger.debug('AuthService not initialized in error handler');
      return;
    }

    logger.debug('AuthService is available, scheduling signOut()');

    // Use setTimeout(0) to break out of the fetch interceptor call stack
    // This ensures the auth state change can properly propagate to React Navigation
    setTimeout(async () => {
      try {
        logger.debug('Executing deferred signOut()');
        // Silent logout - navigation will automatically redirect to login
        await this.authService!.signOut();
        logger.debug('✅ User logged out successfully due to deleted account or session expiry');
      } catch (error) {
        logger.debug('❌ Failed to logout deleted user:', error);
      }
    }, 0);

    logger.debug('signOut() scheduled, continuing fetch interceptor');
  }

  /**
   * Show info dialog for network/outage errors
   */
  private showNetworkDialog(title: string, message: string): void {
    logger.debug(`Attempting to show dialog - Title: ${title}`);

    if (!this.dialog) {
      logger.debug('Dialog not initialized in error handler');
      logger.debug(`Dialog message that would be shown - ${title}: ${message}`);
      return;
    }

    logger.debug(`Showing info dialog: ${title}`);
    this.dialog.alert(title, message, 'info');
  }

  /**
   * Restore original fetch (for testing or cleanup)
   */
  uninstallInterceptor(): void {
    global.fetch = this.originalFetch;
  }
}

// Export singleton instance
export const supabaseErrorHandler = SupabaseErrorHandler.getInstance();
